import React, { useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronLeft, ChevronRight, FileImage, Film, Link2, Plus, X } from 'lucide-react';
import { useEffect } from 'react';
import { ArtistApplicationStatus, PcUser, approvePreviewBandApplication, cancelArtistApplication, queryArtistApplication, submitArtistApplication, uploadPcFile } from '../lib/pcApi';

type ArtistType = 'バンド' | 'アイドル' | 'シンガー' | 'VTuber';
type PlatformLink = { platform: string; url: string };

const steps = ['タイプを選択', '情報を入力', '規約に同意', '申請完了'];
const platformOptions = ['YouTube', 'X（Twitter）', 'TikTok', 'Bilibili'];

export default function BandRecruitment({ user, onLoginRequested }: { user: PcUser | null; onLoginRequested: () => void }) {
  const [step, setStep] = useState(0);
  const [artistType, setArtistType] = useState<ArtistType | ''>('');
  const [bandName, setBandName] = useState('');
  const [platforms, setPlatforms] = useState<PlatformLink[]>([{ platform: 'YouTube', url: '' }]);
  const [email, setEmail] = useState('');
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const [screenshotKeys, setScreenshotKeys] = useState<string[]>([]);
  const [pendingScreenshots, setPendingScreenshots] = useState<File[]>([]);
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
  const [screenshotError, setScreenshotError] = useState('');
  const screenshotOwner = useRef<string | null>(null);
  const [introVideo, setIntroVideo] = useState<File | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [application, setApplication] = useState<ArtistApplicationStatus | null>(null);
  const [statusCheckedFor, setStatusCheckedFor] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(Boolean(user));
  const [statusQueryError, setStatusQueryError] = useState('');
  const [statusRefresh, setStatusRefresh] = useState(0);
  const [retryApplication, setRetryApplication] = useState(false);
  const [busy, setBusy] = useState(false);
  const [apiError, setApiError] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    const previews = pendingScreenshots.map((file) => URL.createObjectURL(file));
    setPendingPreviews(previews);
    return () => previews.forEach((url) => URL.revokeObjectURL(url));
  }, [pendingScreenshots]);

  useEffect(() => {
    const identity = user ? `${user.preview ? 'preview' : 'real'}:${user.userId}` : null;
    if (screenshotOwner.current && screenshotOwner.current !== identity) {
      screenshotOwner.current = null;
      setScreenshots([]);
      setScreenshotKeys([]);
    }
  }, [user?.userId, user?.preview]);

  const identity = user ? `${user.preview ? 'preview' : 'real'}:${user.userId}` : null;

  useEffect(() => {
    if (!identity) {
      setApplication(null);
      setStatusCheckedFor(null);
      setStatusQueryError('');
      setStatusLoading(false);
      setRetryApplication(false);
      return;
    }
    let active = true;
    setStatusLoading(true);
    setStatusQueryError('');
    setRetryApplication(false);
    setApplication(null);
    queryArtistApplication()
      .then((result) => { if (active) setApplication(result); })
      .catch((reason) => { if (active) setStatusQueryError(reason instanceof Error ? reason.message : '申請状況の取得に失敗しました。'); })
      .finally(() => {
        if (active) {
          setStatusCheckedFor(identity);
          setStatusLoading(false);
        }
      });
    return () => { active = false; };
  }, [identity, statusRefresh]);

  const checkingStatus = Boolean(user) && (statusLoading || statusCheckedFor !== identity);
  const showStatus = Boolean(user && application) && !(retryApplication && [3, 4].includes(Number(application?.auditStatus)));

  const isEmailValid = useMemo(() => /^\S+@\S+\.\S+$/.test(email), [email]);
  const hasPlatformLink = platforms.some((item) => /^https?:\/\/[^\s.]+\.[^\s]+$/i.test(item.url.trim()));
  const hasScreenshot = screenshots.length > 0 && screenshotKeys.length === screenshots.length;
  const missingFields = [
    ...(!bandName.trim() ? ['アーティスト名'] : []),
    ...(!hasPlatformLink ? ['有効なSNSチャンネルのURL（1つ以上）'] : []),
    ...(!hasScreenshot ? ['SNSプロフィールのスクリーンショット'] : []),
    ...(!isEmailValid ? [email.trim() ? '有効な連絡用メールアドレス' : '連絡用メールアドレス'] : []),
  ];
  const canContinue = step === 0
    ? Boolean(artistType)
    : step === 1
      ? missingFields.length === 0
      : step === 2
        ? agreed
        : true;

  const updatePlatform = (index: number, key: keyof PlatformLink, value: string) => {
    setPlatforms((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item));
  };

  const addPlatform = () => {
    if (platforms.length < 4) setPlatforms((current) => [...current, { platform: 'X（Twitter）', url: '' }]);
  };

  const handleScreenshots = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected: File[] = Array.from(event.target.files || []);
    event.target.value = '';
    if (!selected.length) return;
    if (selected.length > 1) {
      setApiError('アップロードできる写真は1枚までです。');
      return;
    }
    if (selected.some((file) => !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size === 0 || file.size > 5 * 1024 * 1024)) {
      setApiError('画像はPNG / JPG / WebP形式、5MB以下のみ対応しています。');
      return;
    }
    setApiError('');
    setScreenshotError('');
    setPendingScreenshots(selected);
  };

  const confirmScreenshots = async () => {
    if (!pendingScreenshots.length || busy) return;
    if (!user) {
      setScreenshotError('まずはQRコードでログインし、ログイン後にスクリーンショットのアップロードを確定してください。');
      onLoginRequested();
      return;
    }
    setBusy(true);
    setScreenshotError('');
    const uploadedKeys: string[] = [];
    try {
      for (const file of pendingScreenshots) {
        uploadedKeys.push(await uploadPcFile(file, 'artist', true));
      }
      setScreenshots(pendingScreenshots);
      setScreenshotKeys(uploadedKeys);
      screenshotOwner.current = `${user.preview ? 'preview' : 'real'}:${user.userId}`;
      setPendingScreenshots([]);
      setApiError('');
    } catch (reason) {
      setScreenshotError(reason instanceof Error ? reason.message : 'スクリーンショットのアップロードに失敗しました。もう一度お試しください。');
    } finally {
      setBusy(false);
    }
  };

  const handleVideo = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file && (!['video/mp4', 'video/quicktime'].includes(file.type) || file.size > 100 * 1024 * 1024)) {
      setApiError('紹介動画はMP4 / MOV形式、100MB以下のみ対応しています。');
      return;
    }
    setApiError('');
    if (!file) { setIntroVideo(null); return; }
    const video = document.createElement('video');
    const previewUrl = URL.createObjectURL(file);
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(previewUrl);
      if (video.duration > 60 || !Number.isFinite(video.duration)) {
        setIntroVideo(null);
        setApiError('紹介動画は1分以内のものを指定してください。');
      } else setIntroVideo(file);
    };
    video.onerror = () => { URL.revokeObjectURL(previewUrl); setIntroVideo(null); setApiError('動画の長さを取得できませんでした。別のファイルをお試しください。'); };
    video.src = previewUrl;
  };

  const continueFlow = async () => {
    if (!canContinue) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    if (step !== 2) { setStep((current) => Math.min(3, current + 1)); return; }
    if (!user) { onLoginRequested(); return; }
    setBusy(true);
    setApiError('');
    try {
      const introVideoKey = introVideo ? await uploadPcFile(introVideo, 'artist') : undefined;
      const submission = {
        artistType: { バンド: 1, シンガー: 2, アイドル: 3, VTuber: 4 }[artistType as ArtistType],
        artistName: bandName.trim(),
        socialLinks: platforms.filter((item) => item.url.trim()).map((item) => ({
          platform: item.platform === 'X（Twitter）' ? 'X' : item.platform,
          url: item.url.trim(),
        })),
        socialProfileUrl: screenshotKeys,
        contactEmail: email.trim(),
        ...(introVideoKey ? { introVideoUrl: introVideoKey } : {}),
      };
      await submitArtistApplication(submission);
      setApplication({ ...submission, auditStatus: 1 });
      setRetryApplication(false);
      setStep(0);
    } catch (reason) {
      setApiError(reason instanceof Error ? reason.message : 'アーティスト申請の送信に失敗しました。時間をおいて再度お試しください。');
    } finally { setBusy(false); }
  };

  const withdrawApplication = async () => {
    if (!user) { onLoginRequested(); return; }
    setBusy(true); setApiError('');
    try {
      await cancelArtistApplication();
      setApplication((current) => current ? { ...current, auditStatus: 4 } : null);
      setRetryApplication(false);
      setStep(0);
      setStatusRefresh((current) => current + 1);
    } catch (reason) { setApiError(reason instanceof Error ? reason.message : '申請の取り消しに失敗しました。'); }
    finally { setBusy(false); }
  };

  return (
    <section id="recruit-page" className="flex flex-1 flex-col justify-end bg-[#080A0A] px-4 pb-10 pt-24 text-white md:px-8">
      <div className="mx-auto w-full max-w-5xl">
        {user && <div className="mb-5 rounded-xl border border-[#00F5D4]/20 bg-[#00F5D4]/[0.05] px-4 py-3 text-xs text-white/70">現在のアカウント：{user.nickName}（UID：{user.userId}）</div>}
        <header className="mb-7 text-center">
          <h1 className="text-2xl font-black tracking-[0.04em] text-[#00F5D4] md:text-3xl">Koemori Studio</h1>
          <p className="mt-1 text-xs text-white/45">アーティスト登録 · Artist Onboarding</p>
        </header>

        {checkingStatus ? (
          <div role="status" className="rounded-2xl border border-white/[0.08] bg-[#111817] px-6 py-12 text-center text-sm text-white/60">申請状況を確認中…</div>
        ) : user && statusQueryError ? (
          <div role="alert" className="rounded-2xl border border-red-500/30 bg-[#111817] px-6 py-10 text-center">
            <h2 className="text-lg font-bold">申請状況を確認できません</h2>
            <p className="mt-2 text-sm text-red-300">{statusQueryError}</p>
            <p className="mt-2 text-xs text-white/45">重複申請を防ぐため、ステータスが確認できるまで申請フォームは表示されません。</p>
            <button type="button" onClick={() => setStatusRefresh((current) => current + 1)} className="mt-5 rounded-lg bg-[#00F5D4] px-5 py-2.5 text-sm font-bold text-[#071C17]">再読み込み</button>
          </div>
        ) : application && showStatus ? (
          <div aria-live="polite" className="rounded-2xl border border-white/[0.08] bg-[#111817] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.35)] md:p-8">
            <div className="mx-auto max-w-2xl text-center">
              <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full text-3xl ${application.auditStatus === 2 ? 'bg-[#00F5D4]/15 text-[#00F5D4]' : application.auditStatus === 3 ? 'bg-red-500/15 text-red-300' : 'bg-[#55B9FF]/15 text-[#55B9FF]'}`}>{application.auditStatus === 2 ? '✓' : application.auditStatus === 3 ? '×' : application.auditStatus === 4 ? '—' : '…'}</div>
              <h2 className="mt-5 text-xl font-black">申請ステータス</h2>
              <p className="mt-2 text-sm text-white/55">{application.auditStatus === 1 ? '申請を受け付けました。現在確認中です。連絡用メールアドレス宛ての通知をご確認ください。' : application.auditStatus === 2 ? 'アーティスト申請が承認されました。' : application.auditStatus === 3 ? 'アーティスト申請が承認されませんでした。理由をご確認の上、再度お申し込みください。' : application.auditStatus === 4 ? 'この申請は取り消されました。再申請が可能です。' : 'ステータスを判別できません。しばらく待ってから再度お試しください。'}</p>
              <div className="mx-auto mt-6 max-w-lg overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] text-left text-xs">
                <SummaryRow label="申請状況" value={({ 1: '確認中', 2: '承認済み', 3: '見送り', 4: 'キャンセル済み' } as Record<number, string>)[application.auditStatus] || 'ステータス確認中'} />
                <SummaryRow label="アーティスト名" value={application.artistName || '-'} />
                <SummaryRow label="アーティストタイプ" value={({ 1: 'バンド', 2: 'シンガー', 3: 'アイドル', 4: 'VTuber' } as Record<number, string>)[Number(application.artistType)] || '-'} />
                {application.createTime && <SummaryRow label="申請日時" value={application.createTime} />}
                {application.rejectReason && <SummaryRow label="却下理由" value={application.rejectReason} />}
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <button type="button" disabled={busy} onClick={() => setStatusRefresh((current) => current + 1)} className="rounded-lg border border-white/20 px-5 py-2.5 text-sm font-bold disabled:opacity-50">状態を更新</button>
                {application.auditStatus === 1 && <button type="button" disabled={busy} onClick={() => void withdrawApplication()} className="rounded-lg border border-red-400/30 px-5 py-2.5 text-sm font-bold text-red-300 disabled:opacity-50">{busy ? 'キャンセル中…' : '申請を取り消す'}</button>}
                {[3, 4].includes(Number(application.auditStatus)) && <button type="button" onClick={() => { setStep(0); setRetryApplication(true); }} className="rounded-lg bg-[#00F5D4] px-5 py-2.5 text-sm font-bold text-[#071C17]">再申請する</button>}
                {user?.preview && application.auditStatus === 1 && Number(application.artistType) === 1 && <button type="button" onClick={() => { try { setApplication(approvePreviewBandApplication()); setApiError(''); } catch (reason) { setApiError(reason instanceof Error ? reason.message : '承認に失敗しました。'); } }} className="rounded-lg bg-amber-200 px-5 py-2.5 text-sm font-bold text-[#231C0C]">承認</button>}
              </div>
            </div>
          </div>
        ) : <>
        <div className="mb-6 grid grid-cols-2 gap-2 rounded-2xl sm:grid-cols-4 border border-white/[0.08] bg-[#101413] p-3 md:gap-3 md:p-4">
          {steps.map((label, index) => {
            const active = index === step;
            const complete = index < step;
            return (
              <div key={label} className="relative flex min-w-0 items-center gap-2 text-[10px] font-bold md:text-xs">
                {index > 0 && <span className={`absolute -left-3 top-1/2 hidden h-px w-2 -translate-y-1/2 md:block ${complete ? 'bg-[#00F5D4]' : 'bg-white/15'}`} />}
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] ${active ? 'bg-gradient-to-r from-[#00F5D4] to-[#55B9FF] shadow-[0_0_18px_rgba(0,245,212,0.45)]' : complete ? 'bg-[#00F5D4] text-[#07140D]' : 'border border-white/20 bg-white/5 text-white/45'}`}>
                  {complete ? <Check className="h-3.5 w-3.5" /> : index + 1}
                </span>
                <span className={`truncate ${active || complete ? 'text-white' : 'text-white/40'}`}>{label}</span>
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl border border-white/[0.08] bg-[#111817] p-5 shadow-[0_24px_70px_rgba(0,0,0,0.35)] md:p-7">
          {step === 0 && (
            <div>
              <h2 className="text-lg font-black">アーティストのタイプを選択</h2>
              <p className="mt-2 text-xs text-white/45">Koemoriでは様々なジャンルのアーティストを募集しています。最も当てはまるタイプを選択してください。</p>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
                {(['バンド', 'アイドル', 'シンガー', 'VTuber'] as ArtistType[]).map((type) => (
                  <button key={type} type="button" onClick={() => setArtistType(type)} className={`rounded-xl border p-5 text-center transition-all ${artistType === type ? 'border-[#EA42D2] bg-[#EA42D2]/15 shadow-[0_0_24px_rgba(234,66,210,0.2)]' : 'border-white/10 bg-white/[0.03] hover:border-white/25'}`}>
                    <span className="text-3xl" aria-hidden="true">{type === 'バンド' ? '🎸' : type === 'アイドル' ? '⭐' : type === 'シンガー' ? '🎤' : '🎮'}</span>
                    <span className="mt-3 block text-sm font-bold">{type}</span>
                    <span className="mt-1 block text-[10px] text-white/40">{type === 'バンド' ? 'グループ、バンド、音楽ユニット' : type === 'アイドル' ? 'アイドル、ソロアーティスト' : type === 'シンガー' ? 'シンガー、ボーカリスト' : 'バーチャル配信者 / VTuber'}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="text-lg font-black">アーティスト情報の入力</h2>
              <p className="mt-2 text-xs text-white/45">入力いただいた情報は、Koemori運営による本人確認および活動実績の確認に使用されます。</p>
              <div className="mt-6 space-y-5">
                <label className="block text-sm font-bold">アーティスト名 <span className="text-[#55B9FF]">*</span><input value={bandName} onChange={(event) => setBandName(event.target.value)} placeholder="例：Benson Band" className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.06] px-4 py-3 text-sm outline-none transition focus:border-[#00F5D4]" /></label>
                <div>
                  <label className="text-sm font-bold">SNSチャンネルURL <span className="text-[#55B9FF]">*</span></label>
                  <div className="mt-2 space-y-2">
                    {platforms.map((item, index) => (
                      <div key={`${item.platform}-${index}`} className="flex flex-col gap-2 sm:flex-row">
                        <select value={item.platform} onChange={(event) => updatePlatform(index, 'platform', event.target.value)} className="w-full rounded-lg sm:w-40 border border-white/10 bg-[#292934] px-2 text-xs outline-none focus:border-[#00F5D4] md:w-40">
                          {platformOptions.map((option) => <option key={option}>{option}</option>)}
                        </select>
                        <div className="relative flex-1"><Link2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/30" /><input value={item.url} onChange={(event) => updatePlatform(index, 'url', event.target.value)} placeholder="https://..." className="w-full rounded-lg border border-white/10 bg-white/[0.06] py-3 pl-9 pr-3 text-sm outline-none focus:border-[#00F5D4]" /></div>
                      </div>
                    ))}
                  </div>
                  {platforms.length < 4 && <button type="button" onClick={addPlatform} className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-[#00F5D4]/50 py-2 text-xs font-bold text-[#00F5D4] hover:bg-[#00F5D4]/10"><Plus className="h-3.5 w-3.5" />SNSを追加</button>}
                  <p className="mt-2 text-[10px] text-white/40">1つ以上のプラットフォームを登録してください（YouTube、X（旧Twitter）、TikTok、Bilibiliなどに対応）</p>
                </div>
                <div>
                  <label className="text-sm font-bold">SNSプロフィールのスクリーンショット <span className="text-[#55B9FF]">*</span></label>
                  <label className="mt-2 flex min-h-24 cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-white/20 bg-white/[0.02] text-center hover:border-[#00F5D4]/70">
                    <FileImage className="h-6 w-6 text-white/35" />
                    <span className="mt-2 text-xs text-white/70">スクリーンショットを選択し、プレビュー確認後にアップロードを確定してください</span>
                    <span className="mt-1 text-[11px] font-bold text-amber-200/80">アップロードできる写真は1枚までです。</span>
                    <span className="mt-1 text-[10px] text-white/35">PNG / JPG / WebP、1枚、5MB以下</span>
                    <input type="file" multiple accept="image/png,image/jpeg,image/webp" onChange={handleScreenshots} className="hidden" disabled={busy} />
                  </label>
                  {hasScreenshot && <p className="mt-2 text-xs text-[#00F5D4]">アップロード確定済み： {screenshots.length} 枚：{screenshots.map((file) => file.name).join('、')}</p>}
                </div>
                <label className="block text-sm font-bold">連絡用メールアドレス <span className="text-[#55B9FF]">*</span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="yourname@example.com" className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.06] px-4 py-3 text-sm outline-none focus:border-[#00F5D4]" /><span className="mt-1 block text-[10px] font-normal text-white/40">Koemori運営よりこちらのメールアドレス宛にご連絡いたします。お間違いのないようご確認ください。</span></label>
                <div><label className="text-sm font-bold">紹介動画 <span className="font-normal text-white/35">（任意・推奨）</span></label><label className="mt-2 flex min-h-24 cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed border-white/20 bg-white/[0.02] text-center hover:border-[#00F5D4]/70"><Film className="h-6 w-6 text-white/35" /><span className="mt-2 text-xs text-white/70">クリックまたはドラッグ＆ドロップで紹介動画をアップロード</span><span className="mt-1 text-[10px] text-white/35">MP4 / MOV対応、1分以内、100MB以下</span><input type="file" accept="video/mp4,video/quicktime" onChange={handleVideo} className="hidden" /></label>{introVideo && <p className="mt-2 text-xs text-[#00F5D4]">選択中：{introVideo.name}</p>}</div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="text-lg font-black">Koemori アーティスト規約</h2>
              <p className="mt-2 text-xs text-white/45">以下の条項をよくお読みいただき、内容をご確認の上「利用規約に同意する」にチェックを入れてください。</p>
              <div className="mt-5 max-h-72 overflow-y-auto rounded-xl border border-white/10 bg-[#171720] p-5 text-xs leading-7 text-white/65">
                <p className="font-bold text-white">1. 参加資格</p><p>1.1 登録者は、適法なアーティストまたはアーティストグループであり、コンテンツに関する完全な著作権または正当な利用許諾を有している必要があります。</p><p>1.2 登録者は、真正かつ有効なSNS等の活動実績資料を提出するものとし、Koemoriはその内容を確認する権利を有します。</p><p>1.3 申請者およびグループメンバーが、同一の区分で重複して申請することはできません。</p>
                <p className="mt-4 font-bold text-white">2. コンテンツの著作権</p><p>2.1 登録者がKoemoriプラットフォーム上で公開した歌唱作品の著作権は、登録者に帰属します。</p><p>2.2 登録者はKoemoriに対し、本プラットフォームおよびその提携チャンネルでの配信・表示を目的とした、非独占的、再許諾可能、かつ全世界を対象とする公衆送信権を付与するものとします。</p><p>2.3 登録者は、登録申請に必要なSNSプロフィールのスクリーンショット、紹介動画、原曲・伴奏・歌詞などのファイルをアップロードできます。アップロードするすべてのファイルについて、正当な権利または必要な利用許諾を有し、第三者の権利を侵害しないことを保証するものとします。</p>
                <p className="mt-4 font-bold text-white">3. 確認およびご連絡について</p><p>申請受付後、Koemori運営より1〜3営業日以内にメールにてご連絡いたします。申請結果は公式からの通知をもって代えさせていただきます。</p>
              </div>
              <label className="mt-4 flex cursor-pointer items-center gap-3 rounded-lg bg-white/[0.05] px-3 py-3 text-xs font-bold"><input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} className="h-4 w-4 accent-[#00F5D4]" />「Koemori アーティスト規約」のすべての条項に同意します</label>
            </div>
          )}

          {step === 3 && (
            <div className="py-8 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#00F5D4] to-[#55B9FF] shadow-[0_0_28px_rgba(0,245,212,0.35)]"><Check className="h-9 w-9 text-white" /></div>
              <h2 className="mt-5 text-xl font-black text-[#00F5D4]">申請が完了しました</h2>
              <p className="mx-auto mt-3 max-w-xl text-xs leading-6 text-white/60">申請をサーバーに送信しました。申請結果が出るまで今しばらくお待ちください。進捗状況はメールにてお知らせいたします。</p>
              <div className="mx-auto mt-6 max-w-md overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] text-left text-xs"><SummaryRow label="アーティストタイプ" value={artistType || '-'} /><SummaryRow label="アーティスト名" value={bandName || '-'} /><SummaryRow label="SNSプラットフォーム" value={platforms.find((item) => item.url)?.platform || '-'} /><SummaryRow label="連絡用メールアドレス" value={email || '-'} /><SummaryRow label="プロフィール画像" value={`${screenshots.length} 枚`} /><SummaryRow label="紹介動画" value={introVideo ? 'アップロード済み' : '未アップロード'} /></div>
              <div className="mt-5 text-xs text-white/60">{application ? `現在の${user?.preview ? 'プレビュー' : '認証'}ステータス：${({ 1: '確認中', 2: '承認済み', 3: '見送り', 4: 'キャンセル済み' } as Record<number, string>)[application.auditStatus] || '不明'}` : '後ほどこのページに戻って申請状況をご確認いただけます。'}</div>
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">{application?.auditStatus === 1 && <button type="button" disabled={busy} onClick={withdrawApplication} className="rounded-lg bg-[#491827] px-7 py-3 text-sm font-bold text-[#FF526C] hover:bg-[#642037] disabled:opacity-50">申請を取り消す</button>}<button type="button" onClick={() => setStep(0)} className="rounded-lg bg-gradient-to-r from-[#6B5CFF] to-[#00F5D4] px-10 py-3 text-sm font-bold shadow-[0_0_22px_rgba(0,245,212,0.22)]">完了</button></div>
            </div>
          )}
        </div>

        {step < 3 && (
          <>
            {showValidation && !canContinue && step > 0 && (
              <p role="alert" className="mt-5 rounded-lg border border-[#EA42D2]/40 bg-[#EA42D2]/10 px-4 py-3 text-sm text-white">
                {step === 1 ? `未入力の項目があります：${missingFields.join('、')}。紹介動画は任意です。` : '利用規約をご確認の上、同意のチェックを入れてください。'}
              </p>
            )}
            <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
              {step > 0 && (
                <button type="button" onClick={() => { setShowValidation(false); setStep((current) => Math.max(0, current - 1)); }} className="flex w-full items-center justify-center gap-1 whitespace-nowrap rounded-lg border border-white/10 bg-white/[0.06] px-5 py-3 text-xs font-bold text-white/70 hover:border-white/25 sm:w-32 sm:shrink-0">
                  <ChevronLeft className="h-4 w-4" />戻る
                </button>
              )}
              <button type="button" disabled={busy || (step === 0 && !canContinue) || (step === 2 && [1, 2].includes(application?.auditStatus ?? 0))} onClick={() => void continueFlow()} className="flex w-full min-w-0 flex-1 items-center justify-center gap-1 whitespace-nowrap rounded-lg bg-gradient-to-r from-[#6B5CFF] to-[#00F5D4] px-5 py-3 text-sm font-bold shadow-[0_0_24px_rgba(0,245,212,0.2)] transition disabled:cursor-not-allowed disabled:bg-none disabled:bg-white/10 disabled:text-white/30 disabled:opacity-100">
                {busy ? 'アップロード・提出中…' : step === 2 ? 'アーティスト申請を送信' : '次へ'}<ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
        </>}
      </div>
      {pendingScreenshots.length > 0 && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4" role="dialog" aria-modal="true" aria-label="SNSプロフィールのスクリーンショットを確認">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-white/15 bg-[#111817] p-5 shadow-2xl md:p-7">
            <h2 className="text-lg font-black">プレビューを確認して確定</h2>
            <p className="mt-2 text-xs text-white/55">確定後にサーバー（OSS）へアップロードされます。アップロードと正常な読み込みが確認できた後、申請を続行できます。</p>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {pendingPreviews.map((url, index) => (
                <div key={url} className="overflow-hidden rounded-xl border border-white/10 bg-black/20">
                  <img src={url} alt={`スクリーンショットのプレビュー ${index + 1}`} className="aspect-square w-full object-contain" />
                  <p className="truncate px-2 py-2 text-xs text-white/65" title={pendingScreenshots[index]?.name}>{pendingScreenshots[index]?.name}</p>
                </div>
              ))}
            </div>
            {screenshotError && <p role="alert" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">{screenshotError}</p>}
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" disabled={busy} onClick={() => { setPendingScreenshots([]); setScreenshotError(''); }} className="rounded-lg border border-white/20 px-5 py-2.5 text-sm disabled:opacity-50">キャンセル</button>
              <button type="button" disabled={busy} onClick={() => void confirmScreenshots()} className="rounded-lg bg-[#00F5D4] px-5 py-2.5 text-sm font-bold text-[#071C17] disabled:opacity-50">{busy ? 'アップロード・検証中…' : '確定してアップロード'}</button>
            </div>
          </div>
        </div>
      )}
      {apiError && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="artist-error-title"
          onClick={() => setApiError('')}
        >
          <div
            className="relative w-full max-w-md rounded-2xl border border-red-400/30 bg-[#151817] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.65)]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setApiError('')}
              className="absolute right-4 top-4 rounded-full p-1 text-white/45 transition hover:bg-white/10 hover:text-white"
              aria-label="閉じる"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-red-500/15 text-red-300">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <h2 id="artist-error-title" className="mt-4 pr-8 text-lg font-black">入力内容をご確認ください</h2>
            <p role="alert" className="mt-3 text-sm leading-6 text-white/70">{apiError}</p>
            <button
              type="button"
              onClick={() => setApiError('')}
              className="mt-6 w-full rounded-lg bg-[#00F5D4] px-5 py-3 text-sm font-bold text-[#071C17] transition hover:brightness-110"
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return <div className="flex items-center justify-between gap-4 border-b border-white/[0.07] px-4 py-2.5 last:border-b-0"><span className="text-white/45">{label}</span><span className="max-w-[70%] truncate text-right font-bold text-white">{value}</span></div>;
}

